import { authApi } from "#/api/authApi";
import { GoogleSignInButton } from "#/components/auth/GoogleSignInButton";
import PasswordStrengthInput from "#/components/inputs/PasswordStrengthInput";
import LanguageSelector from "#/components/ui/LanguageSelector";
import type { RegisterRequest } from "#/types/auth";
import {
  Button,
  Divider,
  NumberInput,
  PasswordInput,
  Stepper,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconBuilding, IconCheck, IconMail, IconUser } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

export default function RegisterPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [googleButtonLoaded, setGoogleButtonLoaded] = useState(false);

  const form = useForm({
    initialValues: {
      companyName: "",
      website: "",
      size: undefined as number | undefined,
      department: "",
      address: "",
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      requirementsMet: false,
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (step === 0) {
        if (!values.companyName.trim()) {
          errors.companyName = t("auth.register.validation.companyNameRequired");
        }
        if (values.website && !/^https?:\/\/.+/.test(values.website)) {
          errors.website = t("auth.register.validation.invalidUrl");
        }
      }

      if (step === 1 && !googleToken) {
        // Only validate these fields if NOT using Google sign-in
        if (!values.name.trim()) {
          errors.name = t("auth.register.validation.nameRequired");
        }
        if (!/^\S+@\S+\.\S+$/.test(values.email)) {
          errors.email = t("auth.register.validation.validEmail");
        }
        if (!values.requirementsMet) {
          errors.password = t("auth.passwordValidation.doesNotMeetRequirements");
        }
        if (values.password.length < 8) {
          errors.password = t("auth.register.validation.passwordMinLength");
        }
        if (values.confirmPassword !== values.password) {
          errors.confirmPassword = t("auth.register.validation.passwordsDoNotMatch");
        }
      }

      return errors;
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterRequest) => {
      return await authApi.register(data);
    },
    onMutate: () => {
      setLoading(true);
      setError(null);
      setEmailError(null);
      setPasswordError(null);
    },
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (err: Error) => {
      const message = err.message || "Registration failed. Please try again.";
      const lowerMessage = message.toLowerCase();

      // Categorize errors
      if (lowerMessage.includes("email") || lowerMessage.includes("already registered")) {
        setEmailError(t("auth.register.emailAlreadyRegistered"));
      } else if (lowerMessage.includes("password")) {
        setPasswordError(message);
      } else {
        setError(message);
      }
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const handleNext = () => {
    const result = form.validate();
    if (result.hasErrors) {
      return;
    }
    setStep(1);
  };

  const handleBack = () => {
    setStep(0);
  };

  const handleSubmit = async () => {
    if (googleToken) {
      // Register with Google - skip form validation
      setLoading(true);
      try {
        await authApi.registerWithGoogle({
          idToken: googleToken,
          companyName: form.values.companyName,
          website: form.values.website || undefined,
          size: form.values.size,
          department: form.values.department || undefined,
          address: form.values.address || undefined,
        });
        // Google accounts don't need email verification - redirect to login
        notifications.show({
          title: t("auth.register.registrationSuccessful"),
          message: t("auth.register.accountCreatedWithGoogle"),
          color: "green",
        });
        window.location.href = "/login";
      } catch (err) {
        const error = err as { response?: { data?: { message?: string } } };
        notifications.show({
          title: t("auth.register.registrationFailed"),
          message: error.response?.data?.message || t("auth.register.failedToRegisterWithGoogle"),
          color: "red",
        });
      } finally {
        setLoading(false);
      }
    } else {
      // Regular registration - validate form
      const result = form.validate();
      if (result.hasErrors) {
        return;
      }

      const data: RegisterRequest = {
        companyName: form.values.companyName,
        website: form.values.website || undefined,
        size: form.values.size,
        department: form.values.department || undefined,
        address: form.values.address || undefined,
        name: form.values.name,
        email: form.values.email,
        password: form.values.password,
      };

      registerMutation.mutate(data);
    }
  };

  const handleGoogleSuccess = (token: string) => {
    setGoogleToken(token);

    // Decode JWT to extract email and name
    try {
      const payload = JSON.parse(atob(token.split('.')[1])) as { name?: string; email?: string };
      form.setValues({
        ...form.values,
        name: payload.name || "",
        email: payload.email || "",
      });
    } catch (err) {
      console.error("Failed to decode Google token", err);
    }
  };

  if (success) {
    return (
      <div className="w-screen min-h-screen flex">
        <img
          src="/login.jpg"
          className="object-cover h-full w-full absolute z-0 top-0 left-0"
          alt="bg"
        />
        <div className="w-full h-full absolute top-0 left-0 z-10 flex items-center justify-center">
          <div className="w-full max-w-md bg-white/90 backdrop-blur-sm rounded-lg p-8 shadow-lg mx-4">
            <div className="flex flex-col items-center gap-6">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <IconMail size={32} color="white" />
              </div>
              <h1 className="text-2xl font-bold text-center text-gray-900">
                {t("auth.register.checkYourEmail")}
              </h1>
              <p className="text-center text-gray-600">
                {t("auth.register.verificationEmailSent")}{" "}
                <span className="font-semibold">{form.values.email}</span>
              </p>
              <p className="text-center text-gray-500 text-sm">
                {t("auth.register.verificationEmailExpiry")}
              </p>
              <Link
                to="/login"
                className="w-full"
              >
                <Button fullWidth color="black" size="md">
                  {t("auth.register.goToLogin")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen min-h-screen flex">
      <img
        src="/login.jpg"
        className="object-cover h-full w-full absolute z-0 top-0 left-0"
        alt="bg"
      />

      <div className="absolute top-4 right-4 z-20 h-11">
        <LanguageSelector />
      </div>

      <div className="w-full h-full absolute top-0 left-0 z-10 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:flex w-full h-screen items-center justify-center">
          <div className="w-fit h-fit m-auto">
            <img
              src="/logo.svg"
              alt="TMA Logo"
              className="w-full h-20 lg:h-32 relative object-contain"
            />
          </div>
        </div>

        <div className="w-full z-10 bg-white/70 backdrop-blur-xs flex flex-col items-center justify-center px-6 lg:px-20 py-8">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex justify-center mb-8">
              <img src="/logo.svg" alt="TMA Logo" className="h-12" />
            </div>

            <h1 className="text-2xl font-bold mb-2 text-gray-900">
              {t("auth.register.title")}
            </h1>
            <p className="text-gray-600 mb-6">
              {t("auth.register.subtitle")}
            </p>

            <Stepper
              active={step}
              size="sm"
              color="black"
              classNames={{
                step: "flex-col gap-1",
                stepIcon: "border-gray-300",
                stepCompletedIcon: "bg-black",
              }}
            >
              <Stepper.Step
                icon={<IconBuilding size={18} />}
                completedIcon={<IconCheck size={18} />}
                label={t("auth.register.stepCompany")}
              />
              <Stepper.Step
                icon={<IconUser size={18} />}
                completedIcon={<IconCheck size={18} />}
                label={t("auth.register.stepDetails")}
              />
            </Stepper>

            <div className="mt-8 flex flex-col gap-4">
              {step === 0 && (
                <>
                  <TextInput
                    label={t("auth.register.companyName")}
                    placeholder={t("auth.register.companyNamePlaceholder")}
                    required
                    {...form.getInputProps("companyName")}
                    disabled={loading}
                    classNames={{
                      input:
                        "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                    }}
                  />

                  <TextInput
                    label={t("auth.register.website")}
                    placeholder={t("auth.register.websitePlaceholder")}
                    {...form.getInputProps("website")}
                    disabled={loading}
                    classNames={{
                      input:
                        "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                    }}
                  />

                  <NumberInput
                    label={t("auth.register.companySize")}
                    placeholder={t("auth.register.companySizePlaceholder")}
                    min={1}
                    {...form.getInputProps("size")}
                    disabled={loading}
                    classNames={{
                      input:
                        "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                    }}
                  />

                  <TextInput
                    label={t("auth.register.department")}
                    placeholder={t("auth.register.departmentPlaceholder")}
                    {...form.getInputProps("department")}
                    disabled={loading}
                    classNames={{
                      input:
                        "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                    }}
                  />

                  <TextInput
                    label={t("auth.register.address")}
                    placeholder={t("auth.register.addressPlaceholder")}
                    {...form.getInputProps("address")}
                    disabled={loading}
                    classNames={{
                      input:
                        "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                    }}
                  />

                  <Button
                    fullWidth
                    mt="md"
                    size="md"
                    onClick={handleNext}
                    color="black"
                  >
                    {t("common.continue")}
                  </Button>
                </>
              )}

              {step === 1 && (
                <>
                  {import.meta.env.VITE_GOOGLE_CLIENT_ID && !googleToken && (
                    <>
                      <GoogleSignInButton
                        onSuccess={handleGoogleSuccess}
                        onLoad={() => setGoogleButtonLoaded(true)}
                        onError={() => {
                          notifications.show({
                            title: t("auth.login.googleSignInFailed"),
                            message: t("auth.login.failedToSignInWithGoogle"),
                            color: "red",
                          });
                        }}
                        text="signup_with"
                      />
                      {googleButtonLoaded && (
                        <Divider
                          my="sm"
                          label={t("common.or")}
                          labelPosition="center"
                          classNames={{ label: "text-gray-600" }}
                        />
                      )}
                    </>
                  )}

                  {googleToken && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <Text size="sm" className="text-green-800 font-medium">
                          {t("auth.register.signingUpWithGoogle")}
                        </Text>
                      </div>
                    </div>
                  )}

                  <TextInput
                    label={t("auth.register.fullName")}
                    placeholder={t("auth.register.fullNamePlaceholder")}
                    required
                    {...form.getInputProps("name")}
                    disabled={loading}
                    classNames={{
                      input:
                        "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                    }}
                  />

                  <TextInput
                    label={t("common.email")}
                    placeholder={t("auth.register.emailPlaceholder")}
                    type="email"
                    required
                    {...form.getInputProps("email")}
                    disabled={loading || !!googleToken}
                    classNames={{
                      input:
                        "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                    }}
                  />

                  {emailError && (
                    <div className="flex flex-col gap-2 -mt-2">
                      <Text color="red.6" size="sm">
                        {emailError}
                      </Text>
                      <Link
                        to="/login"
                        className="text-black text-sm font-semibold underline w-fit"
                      >
                        {t("auth.register.signInInstead")}
                      </Link>
                    </div>
                  )}

                  {!googleToken && (
                    <>
                      <PasswordStrengthInput
                        label={t("auth.login.password")}
                        placeholder={t("auth.register.passwordPlaceholder")}
                        onPasswordChange={(data) => {
                          form.setValues({
                            ...form.values,
                            password: data.value,
                            requirementsMet: data.requirementsMet,
                          });
                        }}
                      />

                      <PasswordInput
                        label={t("auth.register.confirmPassword")}
                        placeholder={t("auth.register.confirmPasswordPlaceholder")}
                        required
                        {...form.getInputProps("confirmPassword")}
                        disabled={loading}
                        classNames={{
                          input:
                            "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                        }}
                      />

                      {passwordError && (
                        <div className="-mt-2">
                          <Text color="red.6" size="sm">
                            {passwordError}
                          </Text>
                        </div>
                      )}
                    </>
                  )}

                  <div className="flex gap-3 mt-4">
                    <Button
                      variant="outline"
                      color="black"
                      size="md"
                      onClick={handleBack}
                      disabled={loading}
                      className="flex-1"
                    >
                      {t("common.back")}
                    </Button>
                    <Button
                      color="black"
                      size="md"
                      onClick={handleSubmit}
                      loading={loading}
                      className="flex-1"
                    >
                      {t("auth.register.register")}
                    </Button>
                  </div>

                  {error && (
                    <div className="mt-2">
                      <Text color="red.6" size="sm">
                        {error}
                      </Text>
                    </div>
                  )}
                </>
              )}

              <div className="text-center mt-4">
                <Text size="sm" c="dimmed">
                  {t("auth.register.alreadyHaveAccount")}{" "}
                  <Link
                    to="/login"
                    className="text-black font-semibold underline"
                  >
                    {t("auth.register.signIn")}
                  </Link>
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
